import React from "react";
import {
	Button,
	ClassNameProps,
	compareObjects,
	compareStringsAsc,
	Conditional,
	Else,
	getObjectValues,
	If,
	makeClassName,
	Row,
	stringToLocalDate,
	StyleProps,
	toGrammaticalNumber,
	useOpenToggle,
} from "@hex-insights/core";
import { Table } from "@hex-insights/tables";
import { Icon } from "../../../../Components";
import { ContractInstallment } from "../../../../Utilities";
import { ContractInstallmentFormat, ModelFormat } from "../../../Format";
import { ContractInstallmentUtilities, ModelUtilities } from "../../../Utilities";
import { PaymentStatusBadge, paymentStatusColors, PaymentStatusIcon } from "../../_General/PaymentStatus";
import { ContractInstallmentActionButtons } from "../ActionButtons";
import styles from "./styles.module.css";

const columns = ["icon", "status", "dueDate", "description", "amount", "actions"];

const columnWidths = {
	icon: "3rem",
	actions: "5rem",
};

export type ContractInstallmentsSummaryAccordionProps = {
	variant: "admin" | "parent";
	contractInstallments: (Pick<ContractInstallment, "id" | "dueDate"> &
		ContractInstallmentUtilities.PrecomputedContractInstallment)[];
} & Partial<ClassNameProps & StyleProps>;

export function ContractInstallmentsSummaryAccordion({
	variant,
	contractInstallments,
	className,
	style,
}: ContractInstallmentsSummaryAccordionProps) {
	const contractInstallmentGroups = React.useMemo(
		() => groupContractInstallmentsByDate(contractInstallments),
		[contractInstallments],
	);

	return (
		<Table
			columns={columns}
			columnWidths={columnWidths}
			className={makeClassName(styles["table"], className)}
			style={style}
		>
			<Table.Header className={styles["table__header"]}>
				<Table.Row>
					<Table.Heading column="icon" noResize></Table.Heading>
					<Table.Heading column="status" noResize>
						Status
					</Table.Heading>
					<Table.Heading column="dueDate" noResize>
						Due
					</Table.Heading>
					<Table.Heading column="description" noResize>
						Description
					</Table.Heading>
					<Table.Heading column="amount" noResize>
						Amount
					</Table.Heading>
					<Table.Heading column="actions" noResize>
						Actions
					</Table.Heading>
				</Table.Row>
			</Table.Header>
			<Table.Body>
				{contractInstallmentGroups.map((contractInstallmentGroup, i) => (
					<ContractInstallmentGroupRow
						key={contractInstallmentGroup.dueDate}
						variant={variant}
						contractInstallmentGroup={contractInstallmentGroup}
						isLast={i === contractInstallmentGroups.length - 1}
					/>
				))}
			</Table.Body>
		</Table>
	);
}

type ContractInstallmentGroupRowProps = {
	variant: "admin" | "parent";
	contractInstallmentGroup: ContractInstallmentDueDateGroup<
		ContractInstallmentsSummaryAccordionProps["contractInstallments"][0]
	>;
	isLast?: boolean;
};

function ContractInstallmentGroupRow({
	variant,
	contractInstallmentGroup,
	isLast = false,
}: ContractInstallmentGroupRowProps) {
	const { isOpen, toggleIsOpen } = useOpenToggle();

	const hasMultipleInstallments = contractInstallmentGroup.contractInstallments.length > 1;
	const singleContractInstallment = contractInstallmentGroup.contractInstallments[0];

	const onClick = React.useCallback(
		(event: React.MouseEvent) => {
			if (!hasMultipleInstallments) {
				return;
			}
			event.stopPropagation();
			toggleIsOpen();
		},
		[hasMultipleInstallments, toggleIsOpen],
	);

	return (
		<React.Fragment key={contractInstallmentGroup.dueDate}>
			{/* TODO hex-core-update remove `as any` */}
			<Table.Row onClick={onClick as any} className={makeClassName(styles["row"], isLast ? styles["row--last"] : "")}>
				<Table.Cell column="icon" className={styles["cell--centered-content"]}>
					<Row justify="center" align="center" className={styles["cell--centered-content__content"]}>
						<PaymentStatusIcon status={contractInstallmentGroup.paymentStatus} size={24} />
					</Row>

					<div
						className={styles["connection-line"]}
						style={
							{
								"--icon-column-width": columnWidths.icon,
								"--payment-status-color": paymentStatusColors[contractInstallmentGroup.paymentStatus].iconColor,
								"--sub-rows": isOpen ? contractInstallmentGroup.contractInstallments.length : 0,
							} as React.CSSProperties
						}
					>
						{/* in this cell to not interfere with the table layout */}
					</div>
				</Table.Cell>
				<Table.Cell column="status" className={styles["cell--centered-content"]}>
					<Row align="center" className={styles["cell--centered-content__content"]}>
						<PaymentStatusBadge
							status={contractInstallmentGroup.paymentStatus}
							className={styles["payment-status-badge"]}
						/>
					</Row>
				</Table.Cell>
				<Table.Cell column="dueDate" oneLine>
					{ContractInstallmentFormat.Fields.dueDate(contractInstallmentGroup.dueDate)}
				</Table.Cell>
				<Table.Cell column="description" oneLine>
					<Conditional>
						<If condition={hasMultipleInstallments}>
							<span>{toGrammaticalNumber("Payment", contractInstallmentGroup.contractInstallments.length, true)}</span>
						</If>
						<Else>
							<span>
								Installment {singleContractInstallment.index + 1} of {singleContractInstallment.totalInstallments}
							</span>
						</Else>
					</Conditional>
				</Table.Cell>
				<Table.Cell column="amount" oneLine numeric className={styles["cell--amount"]}>
					{ModelFormat.money(contractInstallmentGroup.totalAmount)}
				</Table.Cell>
				<Table.Cell column="actions" className={styles["cell--centered-content"]}>
					<Row align="center" className={styles["cell--centered-content__content"]}>
						<Conditional>
							<If condition={hasMultipleInstallments}>
								<Button variant="link" size="small" onClick={onClick} className="button--icon">
									{isOpen ? (
										<Icon.CaretUp size={16} className="block" />
									) : (
										<Icon.CaretDown size={16} className="block" />
									)}
								</Button>
							</If>
							<Else>
								<ContractInstallmentActionButtons
									variant={variant}
									contractInstallmentID={singleContractInstallment.id}
								/>
							</Else>
						</Conditional>
					</Row>
				</Table.Cell>
			</Table.Row>

			<If condition={isOpen}>
				{contractInstallmentGroup.contractInstallments.map((contractInstallment) => (
					<Table.Row key={contractInstallment.id} className={styles["sub-row"]}>
						<Table.Cell
							column="icon"
							className={styles["sub-row__cell"] + " " + styles["sub-row__cell--icon"]}
						></Table.Cell>
						<Table.Cell column="status" className={styles["sub-row__cell"] + " " + styles["cell--centered-content"]}>
							<Row align="center" className={styles["cell--centered-content__content"]}>
								<PaymentStatusBadge
									status={contractInstallment.paymentStatus}
									className={styles["payment-status-badge"]}
								/>
							</Row>
						</Table.Cell>
						<Table.Cell column="dueDate" oneLine className={styles["sub-row__cell"]}>
							{ContractInstallmentFormat.Fields.dueDate(contractInstallment.dueDate)}
						</Table.Cell>
						<Table.Cell column="description" oneLine className={styles["sub-row__cell"]}>
							Installment {contractInstallment.index + 1} of {contractInstallment.totalInstallments}
						</Table.Cell>
						<Table.Cell
							column="amount"
							oneLine
							numeric
							className={styles["sub-row__cell"] + " " + styles["cell--amount"]}
						>
							{ModelFormat.money(contractInstallment.totalAmount)}
						</Table.Cell>
						<Table.Cell column="actions" className={styles["sub-row__cell"] + " " + styles["cell--centered-content"]}>
							<Row align="center" className={styles["cell--centered-content__content"]}>
								<ContractInstallmentActionButtons variant={variant} contractInstallmentID={contractInstallment.id} />
							</Row>
						</Table.Cell>
					</Table.Row>
				))}
			</If>
		</React.Fragment>
	);
}

type ContractInstallmentDueDateGroup<T> = {
	dueDate: ContractInstallment["dueDate"];
	totalAmount: number;
	paymentStatus: ModelUtilities.PaymentStatus;
	contractInstallments: T[];
};

function groupContractInstallmentsByDate<
	T extends Pick<ContractInstallment, "dueDate"> &
		Pick<ContractInstallmentUtilities.PrecomputedContractInstallment, "totalAmount" | "amountDue">,
>(contractInstallments: T[]) {
	const groupMap: Record<string, ContractInstallmentDueDateGroup<T>> = {};
	for (let i = 0; i < contractInstallments.length; i++) {
		const contractInstallment = contractInstallments[i];
		const dueDate = contractInstallment.dueDate;
		const dueDateKey = stringToLocalDate(dueDate, "day").toISOString();
		if (!groupMap[dueDateKey]) {
			groupMap[dueDateKey] = {
				dueDate,
				totalAmount: 0,
				contractInstallments: [],
				paymentStatus: ModelUtilities.PaymentStatus.Upcoming,
			};
		}
		groupMap[dueDateKey].totalAmount += contractInstallment.totalAmount;
		groupMap[dueDateKey].contractInstallments.push(contractInstallment);
		groupMap[dueDateKey].paymentStatus = ContractInstallmentUtilities.getPaymentStatusFromValues(
			contractInstallment.amountDue,
			groupMap[dueDateKey].dueDate,
		);
	}
	return getObjectValues(groupMap).sort(compareObjects("dueDate", compareStringsAsc()));
}
